import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import WidgetWrapper from './WidgetWrapper';

const Image = ({ 
  src = '',
  alt = 'Image',
  fit = 'contain', // 'contain', 'cover', 'fill', 'none', 'scale-down'
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState('');
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!src) {
      setImageSrc('');
      setImageError(false);
      return;
    }

    // Convert file paths to proper image sources
    const processImageSrc = (srcPath) => {
      // If it's already a URL (http/https/data:), use as is
      if (srcPath.startsWith('http') || srcPath.startsWith('data:') || srcPath.startsWith('blob:')) {
        return srcPath;
      }
      
      // For local files, use the backend server to serve images
      // Convert absolute paths to filename only
      if (srcPath.includes('/') || srcPath.includes('\\')) {
        const filename = srcPath.split(/[/\\]/).pop();
        return `http://localhost:5169/${filename}`;
      }
      
      // If filename doesn't start with /, add backend URL
      if (!srcPath.startsWith('/')) {
        return `http://localhost:5169/${srcPath}`;
      }
      
      // Remove leading slash and add backend URL
      const filename = srcPath.startsWith('/') ? srcPath.substring(1) : srcPath;
      return `http://localhost:5169/${filename}`;
    };

    const processedSrc = processImageSrc(src);
    setImageSrc(processedSrc);
    setImageError(false);
  }, [src]);

  const handleImageError = () => {
    setImageError(true);
  };

  if (imageError || !imageSrc) {
    return (
      <WidgetWrapper 
        backgroundColor={backgroundColor}
        foreColor={foreColor}
        borderWidth={borderWidth}
        borderColor={borderColor}
        borderRadius={borderRadius}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        opacity={opacity}
        padding="8px"
        {...props}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '2px dashed #ccc',
            borderRadius: 1
          }}
        >
          <Box sx={{ textAlign: 'center', color: '#999' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>🖼️</Typography>
            <Typography variant="caption" display="block">
              {src ? 'Image not found' : 'No image'}
            </Typography>
            {src && (
              <>
                <Typography variant="caption" display="block" sx={{ fontSize: '10px', mt: 0.5 }}>
                  Original: {src}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: '10px', color: 'orange' }}>
                  Looking for: {imageSrc}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: '9px', mt: 1, color: '#666' }}>
                  Select the widget and use Property Inspector to upload an image
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </WidgetWrapper>
    );
  }

  return (
    <WidgetWrapper 
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      padding={0}
      {...props}
    >
      <Box
        component="img"
        src={imageSrc}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: fit,
          display: 'block'
        }}
        onError={handleImageError}
      />
    </WidgetWrapper>
  );
};

export default Image;